import React, { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import styled from 'styled-components';
import 'react-image-crop/dist/ReactCrop.css';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Dialog = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const DialogTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  padding: 4px;
  line-height: 1;
  
  &:hover {
    color: #333;
  }
`;

const CropContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  max-height: 60vh;
  overflow: auto;
`;

const CropImage = styled.img`
  max-width: 70vw;
  max-height: 50vh;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const DialogActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background-color: #3498db;
    color: white;
    border: none;
    
    &:hover {
      background-color: #2980b9;
    }
  ` : `
    background-color: #f8f9fa;
    color: #333;
    border: 1px solid #d1d5db;
    
    &:hover {
      background-color: #e9ecef;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PreviewContainer = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const PreviewTitle = styled.h4`
  margin: 0 0 10px 0;
  font-size: 14px;
  color: #666;
`;

const PreviewImage = styled.img`
  max-width: 200px;
  max-height: 150px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

interface ImageCropDialogProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onCropComplete: (croppedImageBlob: Blob) => void;
  aspectRatio?: number;
}

const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspectRatio = 6 / 4 // 6x4 aspect ratio
}) => {
  const handleClose = useCallback(() => {
    console.log('Close modal action triggered');
    onClose();
  }, [onClose]);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 90,
    height: 60,
    x: 5,
    y: 20
  });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const imgRef = useRef<HTMLImageElement>(null);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    
    // Calculate optimal crop size based on image dimensions and aspect ratio
    let cropWidth, cropHeight;
    
    if (width / height > aspectRatio) {
      // Image is wider than desired aspect ratio
      cropHeight = Math.min(height * 0.8, 300); // Max 80% of height or 300px
      cropWidth = cropHeight * aspectRatio;
    } else {
      // Image is taller than desired aspect ratio
      cropWidth = Math.min(width * 0.8, 400); // Max 80% of width or 400px
      cropHeight = cropWidth / aspectRatio;
    }
    
    const initialCrop = {
      unit: 'px' as const,
      width: cropWidth,
      height: cropHeight,
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2
    };
    
    setCrop(initialCrop);
    
    // Set the initial crop as completed crop so it can be saved immediately
    const pixelCrop: PixelCrop = {
      unit: 'px',
      width: cropWidth,
      height: cropHeight,
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2
    };
    
    setCompletedCrop(pixelCrop);
    
    // Generate preview inline to avoid dependency issues
    if (imgRef.current) {
      const image = imgRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (ctx) {
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
          image,
          pixelCrop.x * scaleX,
          pixelCrop.y * scaleY,
          pixelCrop.width * scaleX,
          pixelCrop.height * scaleY,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );

        const base64Image = canvas.toDataURL('image/jpeg', 0.9);
        setPreviewUrl(base64Image);
      }
    }
  }, [aspectRatio]);

  const generatePreview = useCallback(async (crop: PixelCrop) => {
    if (!imgRef.current || !crop.width || !crop.height) {
      return;
    }

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    const base64Image = canvas.toDataURL('image/jpeg', 0.9);
    setPreviewUrl(base64Image);
  }, []);

  const handleCropComplete = useCallback((crop: PixelCrop) => {
    setCompletedCrop(crop);
    generatePreview(crop);
  }, [generatePreview]);

  const handleSave = useCallback(async () => {
    console.log('SaveCrop action initiated');
    
    if (!imgRef.current || !completedCrop?.width || !completedCrop?.height) {
      console.log('SaveCrop validation failed: missing image or crop dimensions');
      return;
    }

    console.log('SaveCrop processing with crop dimensions:', {
      width: completedCrop.width,
      height: completedCrop.height,
      x: completedCrop.x,
      y: completedCrop.y
    });

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.log('SaveCrop failed: could not get canvas context');
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    console.log('SaveCrop canvas setup complete, drawing image...');

    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob(
      (blob) => {
        if (blob) {
          console.log('SaveCrop success: blob created with size', blob.size, 'bytes');
          onCropComplete(blob);
          handleClose();
        } else {
          console.log('SaveCrop failed: blob creation failed');
        }
      },
      'image/jpeg',
      0.9
    );
  }, [completedCrop, onCropComplete, handleClose]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Crop Image (6:4 Ratio)</DialogTitle>
          <CloseButton type="button" onClick={handleClose}>&times;</CloseButton>
        </DialogHeader>
        
        <CropContainer>
          <ReactCrop
            crop={crop}
            onChange={(_, percentCrop) => setCrop(percentCrop)}
            onComplete={handleCropComplete}
            aspect={aspectRatio}
            minWidth={60}
            minHeight={60 / aspectRatio}
          >
            <CropImage
              ref={imgRef}
              alt="Crop me"
              src={imageSrc}
              onLoad={onImageLoad}
            />
          </ReactCrop>
        </CropContainer>

        {previewUrl && (
          <PreviewContainer>
            <PreviewTitle>Preview</PreviewTitle>
            <PreviewImage src={previewUrl} alt="Crop preview" />
          </PreviewContainer>
        )}

        <DialogActions>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            type="button"
            variant="primary" 
            onClick={handleSave}
          >
            Save Crop
          </Button>
        </DialogActions>
      </Dialog>
    </Overlay>
  );
};

export default ImageCropDialog;